import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import AnnotatedImage, {
    AutoImageProps,
    FullscreenSvg,
    useResponsiveMargins,
} from '../automotivePage/CarImageComponent';
import { GetResponsiveWidth } from '../common/TabsComponent';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { generateLabelToComponentMap } from './IndustrialDisplayComponent';
import { ApplicationsContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';

// === replace your helpers with these ===

// Strong normalization: NFKC + collapse spaces + lowercase
const normalize = (s: string) =>
    String(s)
        .normalize('NFKC')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLocaleLowerCase();

// Fix: treat "&" or full-width "＆" as "and" instead of removing them
const canonical = (s: string) =>
    normalize(s)
        .replace(/[\uFF06&]+/g, 'and') // replace & / ＆ with "and"
        .replace(/[\s_\-–—+/·•]+/g, '') // remove remaining separators (but not "and")
        .replace(/(?:and|und|et|y|e|ve)$/gi, ''); // optional: strip trailing conjunction if it’s at the end

const DefaultIndustrial: React.FC<AutoImageProps> = ({ onAnnotationClick, contentTree }) => {
    const responsiveMargins = useResponsiveMargins('industrial');
    const t = contentTree.Industrial.Industrial.imageMapLinkTexts;

    // base map from external util (likely: localized label -> internal key)
    const baseMap = useMemo(() => generateLabelToComponentMap(t), [t]);

    // strengthen matching: support normalized label, canonical label, and internal key itself
    const labelToKey = useMemo(() => {
        const alias: Record<string, string> = {};
        Object.entries(baseMap).forEach(([label, key]) => {
            const nLabel = normalize(label);
            alias[nLabel] = key; // normalized label
            alias[canonical(nLabel)] = key; // canonical label (no separators)
            alias[canonical(key)] = key; // internal key itself (and canonical)
        });
        return alias;
    }, [baseMap]);

    const splitLabel = (text: string | undefined, fallback: string[]) =>
        text?.match(/[^&]+&?|[^&]+/g)?.map((s) => s.trim()) ?? fallback;

    const annotations = [
        {
            label: splitLabel(t.gridInfrastructureAndMetering.content, [
                'Grid Infrastructure &',
                'Metering',
            ]),
            pointX: 180,
            pointY: 460,
            labelX: 205,
            labelY: 210,
            lineStartAdjust: 9,
            lineToLabelGap: 20,
            lineToPointGap: -10,
        },
        {
            label: splitLabel(t.renewableAndGreenEnergy.content, ['Renewable &', 'Green Energy']),
            pointX: 330,
            pointY: 280,
            labelX: 352,
            labelY: 120,
            lineStartAdjust: 6,
            lineToLabelGap: 10,
            lineToPointGap: -10,
        },
        {
            label: splitLabel(t.testAndMeasurement.content, ['Test &', 'Measurement']),
            pointX: 490,
            pointY: 460,
            labelX: 510,
            labelY: 20,
            lineStartAdjust: 8,
            lineToLabelGap: 20,
            lineToPointGap: -10,
        },
        {
            label: t.evCharging.content,
            pointX: 608,
            pointY: 290,
            labelX: 630,
            labelY: 140,
            lineStartAdjust: 7,
            lineToLabelGap: 2,
            lineToPointGap: -10,
        },
        {
            label: splitLabel(t.buildingAutomationAndFactoryAutomation.content, [
                'Building Automation &',
                'Factory Automation',
            ]),
            pointX: 790,
            pointY: 430,
            labelX: 810,
            labelY: 225,
            lineStartAdjust: 8,
            lineToLabelGap: 20,
            lineToPointGap: -12,
        },
        {
            label: splitLabel(t.motorDrivesAndTransport.content, ['Motor Drives &', 'Transport']),
            pointX: 894,
            pointY: 500,
            labelX: 920,
            labelY: 315,
            lineStartAdjust: 8,
            lineToLabelGap: 10,
            lineToPointGap: -12,
        },
        {
            label: splitLabel(t.lightingAndDigitalSignage.content, [
                'Lighting &',
                'Digital Signage',
            ]),
            pointX: 995,
            pointY: 480,
            labelX: 1029,
            labelY: 400,
            lineStartAdjust: 6,
            lineToLabelGap: 10,
            lineToPointGap: -16,
        },
        {
            label: t.hvac.content,
            pointX: 680,
            pointY: 780,
            labelX: 699,
            labelY: 870,
            lineStartAdjust: 4,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
        {
            label: splitLabel(t.powerSupply.content, ['Power &', 'Supply']),
            pointX: 540,
            pointY: 610,
            labelX: 555,
            labelY: 770,
            lineStartAdjust: 7,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
    ];

    return (
        <AnnotatedImage
            imageSrc={'/industrual_image/industrial_main.png'}
            annotations={annotations}
            onAnnotationClick={(rawLabel) => {
                const s = Array.isArray(rawLabel) ? rawLabel.join(' ') : String(rawLabel);
                const key = labelToKey[normalize(s)] ?? labelToKey[canonical(s)];
                if (!key) {
                    console.warn('[DefaultIndustrial] Unknown label; could not map to key', {
                        rawLabel,
                        normalized: normalize(s),
                        canonical: canonical(s),
                        available: Object.keys(labelToKey).slice(0, 25),
                    });
                    return; // prevent passing undefined upward
                }
                onAnnotationClick(key);
            }}
            imageStyle={{
                ...responsiveMargins,
                width: '99%',
                height: 'auto',
            }}
            viewBox={'0 0 1180 885'}
            isDefaultImage
        />
    );
};

export interface LabeledSvgSwitchProps {
    onBack: () => void;
    contentTree: ApplicationsContent;
}

/** -------- fade animation configs -------- */
const fadeVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
};
const fadeTransition = { duration: 0.3, ease: 'easeInOut' };

interface svgList {
    label: string;
    link?: string;
}
interface FullscreenSwitchSvgProps {
    svgs: { svgUrl: string; label: string; svgList?: svgList[] }[];
    onBack: () => void;
}

const FullscreenSwitchSvg: React.FC<FullscreenSwitchSvgProps> = ({ svgs, onBack }) => {
    const [currentSvgIndex, setCurrentSvgIndex] = useState(0);
    const { isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const currentSvg = svgs?.[currentSvgIndex];

    return (
        <Box
            sx={{
                width: GetResponsiveWidth,
                height: '100%',
                overflow: 'hidden',
                marginBottom: '2rem',
            }}
        >
            {/* Switch button */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    marginLeft: isScreenSm ? '0.5rem' : isScreenXs ? '0.2rem' : '2rem',
                    marginBottom: '3rem',
                    gap: '1.2rem',
                }}
            >
                {svgs.map((svg, index) => (
                    <React.Fragment key={index}>
                        <Box
                            onClick={() => setCurrentSvgIndex(index)}
                            sx={{
                                cursor: 'pointer',
                                fontSize: isScreenSm ? '1rem' : isScreenXs ? '0.9rem' : '1.2rem',
                                fontFamily: 'Poppins',
                                fontWeight: 700,
                                color: currentSvgIndex === index ? '#E5673E' : '#000',
                                borderBottom:
                                    currentSvgIndex === index ? '1px solid #E5673E' : 'none',
                                paddingBottom: '0.1px',
                                ':hover': {
                                    color: '#E5673E',
                                    borderBottom: '1px solid #E5673E',
                                    fontWeight: 'bold',
                                },
                            }}
                        >
                            {svg.label}
                        </Box>
                        {index < svgs.length - 1 && (
                            <Box sx={{ fontSize: '2rem', color: '#E5673E', userSelect: 'none' }}>
                                |
                            </Box>
                        )}
                    </React.Fragment>
                ))}
            </Box>

            {isScreenSm || isScreenXs ? (
                currentSvg && (
                    <FullscreenSvg
                        onBack={onBack}
                        svgUrl={currentSvg.svgUrl}
                        svgList={currentSvg.svgList}
                    />
                )
            ) : (
                <div
                    style={{
                        minHeight: isScreenMd ? '390px' : isScreenLg ? '500px' : '650px',
                        position: 'relative',
                    }}
                >
                    <AnimatePresence mode="wait">
                        {currentSvg && (
                            <motion.div
                                key={currentSvgIndex}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={fadeVariants}
                                transition={fadeTransition}
                            >
                                <FullscreenSvg
                                    onBack={onBack}
                                    svgUrl={currentSvg.svgUrl}
                                    svgList={currentSvg.svgList}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </Box>
    );
};

const LowVoltDcSwitchAcImage: React.FC<LabeledSvgSwitchProps> = ({ onBack, contentTree }) => {
    const labels = contentTree.Industrial.Industrial.powerSupplyType;
    return (
        <FullscreenSwitchSvg
            svgs={[
                {
                    svgUrl: '/industrual_image/commerical_Digital_Passive_Components.svg',
                    label: labels.lowVoltageDcDcLabel.content,
                },
                {
                    svgUrl: '/automotive_image/commercial_Power_Conversion_AC_DC_relative.svg',
                    label: labels.acDcLabel.content,
                },
            ]}
            onBack={onBack}
        />
    );
};

export { DefaultIndustrial, LowVoltDcSwitchAcImage };
